import { useRef, useState } from "react";
import { UploadCloud, Camera, File, X, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.heif,.csv,.xls,.xlsx";
const MAX = 15 * 1024 * 1024;
const ALLOWED = ["pdf", "jpg", "jpeg", "png", "heic", "heif", "csv", "xls", "xlsx"];

export default function DocumentUpload({ clientId, category = "document", onUploaded }) {
  const inputRef = useRef();
  const camRef = useRef();
  const [drag, setDrag] = useState(false);
  const [queue, setQueue] = useState([]);

  const validate = (file) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED.includes(ext)) return "Unsupported type";
    if (file.size > MAX) return "Over 15MB";
    return null;
  };

  const uploadOne = async (file) => {
    const err = validate(file);
    if (err) {
      toast.error(`${file.name}: ${err}`);
      return;
    }
    const id = Math.random().toString(36).slice(2);
    setQueue((q) => [...q, { id, name: file.name }]);
    const form = new FormData();
    form.append("file", file);
    form.append("category", category);
    try {
      await api.post(`/clients/${clientId}/documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded`);
      onUploaded?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setQueue((q) => q.filter((x) => x.id !== id));
    }
  };

  const handleFiles = (files) => {
    [...files].forEach(uploadOne);
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        data-testid="document-dropzone"
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${
          drag ? "border-[#C9A84A] bg-[#C9A84A]/5" : "border-[#1E294B] hover:border-[#C9A84A]/50"
        }`}
      >
        <UploadCloud className="mx-auto text-[#C9A84A] mb-3" size={34} />
        <p className="text-white font-medium">Drag & drop or tap to upload</p>
        <p className="text-slate-500 text-sm mt-1">PDF, JPG, PNG, HEIC, CSV, XLS, XLSX · max 15MB</p>
        <div className="flex gap-3 justify-center mt-4">
          <button type="button" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
            className="card-surface border border-[#1E294B] rounded-full px-4 py-2 text-sm text-white flex items-center gap-2 hover:bg-[#18243F]" data-testid="choose-files">
            <File size={16} /> Choose files
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); camRef.current?.click(); }}
            className="card-surface border border-[#1E294B] rounded-full px-4 py-2 text-sm text-white flex items-center gap-2 hover:bg-[#18243F]" data-testid="use-camera">
            <Camera size={16} /> Camera
          </button>
        </div>
      </div>
      <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="file-input" />
      <input ref={camRef} type="file" accept="image/*" capture="environment" hidden
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />

      {queue.map((q) => (
        <div key={q.id} className="flex items-center gap-2 text-slate-400 text-sm mt-3">
          <Loader2 className="animate-spin" size={16} /> Uploading {q.name}…
        </div>
      ))}
    </div>
  );
}
